import * as React from 'react';
import { useSelector } from 'react-redux';
import { Input, Outside } from '@cian/ui-kit';
import { debounce } from 'throttle-debounce';

import { getRegionsByText } from './utils';
import {
  LocationPopoverList,
  LocationPopover,
  ILocationItem,
} from '../../../../../../components/redesign/LocationModal/desktop';
import { DEBOUNCE_DELAY } from '../../../../../../constants/delays';
import { selectRegions } from '../../../../../../selectors/regions';
import { IRegion } from '../../../../../../types/regions';

interface ILocationModalInputContainerProps {
  onRegionSelect: (region: IRegion) => void;
}

export const LocationModalInput: React.FC<ILocationModalInputContainerProps> = ({ onRegionSelect }) => {
  const regions = useSelector(selectRegions);

  const [searchList, setSearchList] = React.useState<IRegion[]>([]);
  const [isPopoverOpen, setPopoverOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');

  const isPopoverVisible = Boolean(isPopoverOpen && searchList.length && inputValue.length);

  const closePopover = React.useCallback(() => setPopoverOpen(false), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInputChange = React.useCallback(
    debounce(DEBOUNCE_DELAY, (value: string) => {
      const regionsByText = getRegionsByText(value, regions);
      setSearchList(regionsByText);
    }),
    [regions],
  );

  const handleInputChange = React.useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
      setInputValue(value);
      setPopoverOpen(true);
      debouncedInputChange(value);
    },
    [debouncedInputChange],
  );

  const handleItemSelect = React.useCallback(
    (option: ILocationItem) => {
      onRegionSelect(searchList[option.id]);
      setPopoverOpen(false);
      setInputValue('');
    },
    [searchList, onRegionSelect],
  );

  return (
    <LocationPopover
      isOpen={isPopoverVisible}
      content={
        <Outside onOutside={closePopover}>
          <div>
            <LocationPopoverList
              onItemClick={handleItemSelect}
              options={searchList.map(({ name }, index) => ({ id: index, label: name }))}
            />
          </div>
        </Outside>
      }
    >
      <Input
        onChange={handleInputChange}
        value={inputValue}
        aria-label="Регион или город"
        placeholder="Введите регион или город"
        size="XS"
        data-testid="LocationModalInput"
      />
    </LocationPopover>
  );
};
